import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { PersonBadge } from '@/components/common/PersonBadge';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getWorkTypeFilterField } from '@/features/billing/utils/billingFilterFields';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import {
  getDateFilterField,
  getDateFromToFilterFields,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { filterRowsBySearch } from '@/utils/listSearch';
import { assignedInstitutesQueryOptions, dcrSheetQueryOptions } from '../api/product.api';
import {
  getAssignedInstituteColumns,
  getSheetEntryColumns,
  getSheetExpenseColumns,
} from '../components/ProductColumns';
import { useSheetFilters, type SheetRouteId } from '../hooks/useSheetFilters';
import { NO_SEARCH_MATCH_MESSAGE } from '../constants/product.constants';
import type {
  AssignedInstitute,
  DcrSheetEntry,
  DcrSheetExpense,
  DcrSheetParams,
} from '../types/product.types';
import {
  SHEET_TAB_CARD_SLOTS,
  getDcrEntryStatCards,
  getExpenseStatCards,
  getInstituteVisitStatCards,
} from '../utils/dcrSheetStatCards';

type SheetTab = 'entries' | 'expenses' | 'visited' | 'missed';

const SEARCH_PLACEHOLDER = 'Institute, EIIN or expense';

const toSearchableEntryValues = (entry: DcrSheetEntry) => [entry.instituteName, entry.eiinNumber];
const toSearchableExpenseValues = (expense: DcrSheetExpense) => [
  expense.expenseType,
  expense.vendor,
];
const toSearchableInstituteValues = (institute: AssignedInstitute) => [
  institute.instituteName,
  institute.eiinNumber,
];

interface DcrSheetPageProps {
  routeId: SheetRouteId;
  title: string;
  shouldShowApprovedOnly: boolean;
}

export function DcrSheetPage({ routeId, title, shouldShowApprovedOnly }: DcrSheetPageProps) {
  const controller = useSheetFilters(routeId);
  const hasZonalManager = controller.params.userId !== undefined;
  const [isFilterOpen, setIsFilterOpen] = useState(!hasZonalManager);
  const [tab, setTab] = useState<SheetTab>('entries');

  const sheetParams: DcrSheetParams = {
    ...controller.params,
    dcrTypeId: controller.dcrTypeId,
    showApprovedOnly: shouldShowApprovedOnly,
  };
  const { data: zonalManagers, isLoading: isZonalManagersLoading } = useQuery(
    zonalManagersQueryOptions(),
  );
  const pickedZonalManager = zonalManagers?.find(
    (zonalManager) => zonalManager.id === controller.params.userId,
  );
  const sheet = useQuery(dcrSheetQueryOptions(sheetParams));
  const visited = useQuery(assignedInstitutesQueryOptions(controller.params, true));
  const missed = useQuery(assignedInstitutesQueryOptions(controller.params, false));

  const appliedSearchText = controller.appliedSearchText;
  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: appliedSearchText,
    onApply: controller.applySearchText,
  });
  const visibleEntries = useMemo(
    () => filterRowsBySearch(sheet.data?.entries, appliedSearchText, toSearchableEntryValues),
    [sheet.data, appliedSearchText],
  );
  const visibleExpenses = useMemo(
    () => filterRowsBySearch(sheet.data?.expenses, appliedSearchText, toSearchableExpenseValues),
    [sheet.data, appliedSearchText],
  );
  const visibleVisited = useMemo(
    () => filterRowsBySearch(visited.data, appliedSearchText, toSearchableInstituteValues),
    [visited.data, appliedSearchText],
  );
  const visibleMissed = useMemo(
    () => filterRowsBySearch(missed.data, appliedSearchText, toSearchableInstituteValues),
    [missed.data, appliedSearchText],
  );

  const tabs: TableTab<SheetTab>[] = [
    { id: 'entries', label: 'DCR Entries', count: sheet.data ? visibleEntries.length : undefined },
    { id: 'expenses', label: 'Expenses', count: sheet.data ? visibleExpenses.length : undefined },
    { id: 'visited', label: 'Visited', count: visited.data ? visibleVisited.length : undefined },
    { id: 'missed', label: 'Not Visited', count: missed.data ? visibleMissed.length : undefined },
  ];

  function getEmptyMessage(messageWithoutSearch: string): string {
    return appliedSearchText ? NO_SEARCH_MATCH_MESSAGE : messageWithoutSearch;
  }

  function getActiveTabStats(): { cards: LabeledStat[]; isLoading: boolean } {
    if (tab === 'entries') {
      return { cards: getDcrEntryStatCards(sheet.data), isLoading: sheet.isLoading };
    }
    if (tab === 'expenses') {
      return { cards: getExpenseStatCards(sheet.data), isLoading: sheet.isLoading };
    }
    return {
      cards: getInstituteVisitStatCards(visited.data, missed.data),
      isLoading: visited.isLoading || missed.isLoading,
    };
  }

  const activeTabStats = getActiveTabStats();

  function renderAssignedInstituteTable(isVisited: boolean) {
    const institutes = isVisited ? visited : missed;
    const visibleInstitutes = isVisited ? visibleVisited : visibleMissed;
    if (institutes.error) {
      return (
        <ErrorState
          error={institutes.error}
          fallbackTitle="Could not load the institutes"
          onRetry={() => void institutes.refetch()}
          fill
        />
      );
    }
    return (
      <DataTable
        data={visibleInstitutes}
        columns={getAssignedInstituteColumns(isVisited, appliedSearchText)}
        isLoading={institutes.isLoading}
        className="lg:flex-1"
        stickyColumnId="instituteName"
        emptyMessage={getEmptyMessage(
          isVisited ? 'No assigned institute was visited' : 'Every assigned institute was visited',
        )}
      />
    );
  }

  function renderActiveTable() {
    if (tab === 'entries') {
      return (
        <DataTable
          data={visibleEntries}
          columns={getSheetEntryColumns(appliedSearchText)}
          isLoading={sheet.isLoading}
          className="lg:flex-1"
          stickyColumnId="dcrDate"
          emptyMessage={getEmptyMessage('No DCR was filed')}
        />
      );
    }
    if (tab === 'expenses') {
      return (
        <DataTable
          data={visibleExpenses}
          columns={getSheetExpenseColumns(appliedSearchText)}
          isLoading={sheet.isLoading}
          className="lg:flex-1"
          stickyColumnId="expenseDate"
          emptyMessage={getEmptyMessage('No expense was claimed')}
        />
      );
    }
    return renderAssignedInstituteTable(tab === 'visited');
  }

  function renderSheet() {
    if (!hasZonalManager) {
      return (
        <EmptyState
          kind="pickFirst"
          title="Pick a ZM"
          description="Choose a ZM in Filters to open the sheet."
          fill
        />
      );
    }
    if (sheet.error) {
      return (
        <ErrorState
          error={sheet.error}
          fallbackTitle="Could not load the sheet"
          onRetry={() => void sheet.refetch()}
          fill
        />
      );
    }
    return (
      <>
        <StatCardGrid
          stats={activeTabStats.cards}
          isLoading={activeTabStats.isLoading}
          layoutCardCount={SHEET_TAB_CARD_SLOTS}
        />
        <TableTabs tabs={tabs} activeTabId={tab} onTabChange={setTab} ariaLabel="Sheet sections" />
        {renderActiveTable()}
      </>
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        search={{ value: searchText, onChange: setSearchText, placeholder: SEARCH_PLACEHOLDER }}
        breadcrumbAddon={
          <PersonBadge
            roleLabel="ZM"
            name={pickedZonalManager?.fullName}
            isLoading={hasZonalManager && isZonalManagersLoading}
          />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            {
              ...getZonalManagerFilterField(zonalManagers),
              placeholder: 'Pick a ZM',
              isClearable: false,
              isRequired: true,
            },
            ...(controller.isSingleDay ? [getDateFilterField()] : getDateFromToFilterFields()),
            getWorkTypeFilterField(),
          ]}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {renderSheet()}
    </div>
  );
}
